import React from 'react';

import { Box, Stack, Text, ActionIcon } from '@mantine/core';
import { IconColorSwatch, IconUser, IconLogout, IconX } from '@tabler/icons-react';
import { Sheet } from 'react-modal-sheet';
import { useModule } from '@/hooks/useModule';
import { useModals } from '../modals/modals_provider';
import { setPathnameForReason } from '@/modules/navigation/navigation_module';

interface SettingsSheetProps {
    isOpen: boolean;
    onClose: () => void;
}

export function SettingsSheet({ isOpen, onClose }: SettingsSheetProps) {
    const modals = useModals();
    const mantineModule = useModule('Mantine');
    const loginFlow = useModule('LoginFlow');
    const userModule = useModule('User');

    const logout = loginFlow?.getLoginProvider()?.useLogout();

    const handleLogout = () => {
        if (!logout) return;

        modals.openModal('ConfirmationModal', {
            title: 'Log out of your account?',
            message: '',
            confirmLabel: 'Log Out',
            onConfirm: () => {
                if (!userModule.isOfflineMode.getState()) {
                    logout();
                } else {
                    userModule.isOfflineMode.setState(false);
                    userModule.currentUser.setState(null);
                    setPathnameForReason('redirect_to_main_due_to_logging_out_in_offline_mode', '/main');
                }
                onClose();
            },
        });
    };

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            snapPoints={[0, 0.5]}
            initialSnap={1}
        >
            <Sheet.Container
                style={{
                    backgroundColor: 'var(--mantine-color-body)',
                }}
            >
                <Sheet.Header>
                    <Box
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            padding: '12px 16px',
                        }}
                    >
                        <ActionIcon
                            variant="subtle"
                            size="lg"
                            onClick={onClose}
                        >
                            <IconX size={24} />
                        </ActionIcon>
                    </Box>
                </Sheet.Header>
                <Sheet.Content
                    style={{
                        backgroundColor: 'var(--mantine-color-body)',
                        paddingBottom: 20,
                    }}
                >
                    <Stack gap={0}>
                        <SettingsItem
                            icon={<IconColorSwatch size={20} />}
                            label="Change Theme"
                            onClick={() => {
                                modals.openModal('CustomModal', {
                                    title: 'Change Theme',
                                    form: mantineModule.ChangeThemeModalForm,
                                });
                                onClose();
                            }}
                        />
                        <SettingsItem
                            icon={<IconUser size={20} />}
                            label="Edit profile"
                            onClick={() => {
                                modals.openModal('EditProfileModal', {});
                                onClose();
                            }}
                        />
                        {logout && (
                            <SettingsItem
                                icon={<IconLogout size={20} />}
                                label="Log out"
                                onClick={handleLogout}
                            />
                        )}
                    </Stack>
                </Sheet.Content>
            </Sheet.Container>
            <Sheet.Backdrop onTap={onClose} />
        </Sheet>
    );
}

function SettingsItem({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
    return (
        <Box
            onClick={onClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '16px 20px',
                cursor: 'pointer',
                borderBottom: '1px solid var(--mantine-color-default-border)',
            }}
        >
            {icon}
            <Text size="sm">{label}</Text>
        </Box>
    );
}
